import { create } from "zustand";

import type { MentionReference } from "@/lib/mentions/types";

export type PendingLaunchDocument = {
  publicId: string;
  name: string;
  contentType: string;
};

export type PendingLaunch = {
  text: string;
  documents: PendingLaunchDocument[];
  contextMentions: MentionReference[];
  /** True while post-navigate uploads are still in flight for this launch. */
  documentsPending?: boolean;
};

/**
 * Hand-off for project-home launch: creates a conversation, stashes the first
 * message (and optional uploaded docs) keyed by conversation id, then navigates
 * to the workbench. {@link PendingPromptDrainer} peeks, sends via append; handoff
 * clears when SSE streaming starts ({@link completeLaunchDrainHandoff}). Transient
 * client hand-off only.
 */
interface PendingConversationPromptStore {
  prompts: Record<string, PendingLaunch>;
  setPrompt: (conversationId: string, pending: PendingLaunch | string) => void;
  patchPrompt: (conversationId: string, patch: Partial<PendingLaunch>) => void;
  /** Read without clearing (append may fail; keep stash for retry). */
  peekPrompt: (conversationId: string) => PendingLaunch | undefined;
  /** Returns and clears the pending launch in one step. */
  takePrompt: (conversationId: string) => PendingLaunch | undefined;
}

function normalizePending(pending: PendingLaunch | string): PendingLaunch {
  if (typeof pending === "string") {
    return { text: pending, documents: [], contextMentions: [] };
  }
  return {
    text: pending.text,
    documents: pending.documents ?? [],
    contextMentions: pending.contextMentions ?? [],
    documentsPending: pending.documentsPending,
  };
}

export const usePendingConversationPromptStore = create<PendingConversationPromptStore>((set, get) => ({
  prompts: {},
  setPrompt: (conversationId, pending) =>
    set((s) => ({
      prompts: { ...s.prompts, [conversationId]: normalizePending(pending) },
    })),
  patchPrompt: (conversationId, patch) =>
    set((s) => {
      const current = s.prompts[conversationId];
      if (!current) return s;
      return {
        prompts: {
          ...s.prompts,
          [conversationId]: normalizePending({ ...current, ...patch }),
        },
      };
    }),
  peekPrompt: (conversationId) => get().prompts[conversationId],
  takePrompt: (conversationId) => {
    const pending = get().prompts[conversationId];
    if (pending === undefined) return undefined;
    set((s) => {
      const { [conversationId]: _removed, ...rest } = s.prompts;
      return { prompts: rest };
    });
    return pending;
  },
}));
